import { AGE_GROUPS, PREFECTURES } from "./constants";
import { median, normalizeSearchText } from "./formatters";
import type { FilterState, NormalizedRunner, SortRule } from "../types";

function optionalNumber(value: string): number | null {
  if (value.trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function filterRunners(runners: NormalizedRunner[], filters: FilterState) {
  const query = normalizeSearchText(filters.query.trim());
  const ageMin = optionalNumber(filters.ageMin);
  const ageMax = optionalNumber(filters.ageMax);
  const timeMin = optionalNumber(filters.timeMinHours);
  const timeMax = optionalNumber(filters.timeMaxHours);
  const activityMin = optionalNumber(filters.activityMin);
  const activityMax = optionalNumber(filters.activityMax);
  const dateFrom = filters.dateFrom ? new Date(`${filters.dateFrom}T00:00:00`).getTime() : null;
  const dateTo = filters.dateTo ? new Date(`${filters.dateTo}T23:59:59.999`).getTime() : null;

  return runners.filter((runner) => {
    if (query && !runner.searchNickname.includes(query)) return false;
    if (filters.genders.length && !filters.genders.includes(runner.gender)) return false;
    if (filters.ageGroups.length && !filters.ageGroups.includes(runner.ageGroup)) return false;
    if (filters.locations.length && !filters.locations.includes(runner.locationKind)) return false;
    if (
      filters.prefectures.length &&
      (runner.prefectureCode == null || !filters.prefectures.includes(runner.prefectureCode))
    ) return false;
    if (ageMin != null && (runner.age == null || runner.age < ageMin)) return false;
    if (ageMax != null && (runner.age == null || runner.age > ageMax)) return false;
    if (timeMin != null && runner.totalTimeSeconds < timeMin * 3600) return false;
    if (timeMax != null && runner.totalTimeSeconds > timeMax * 3600) return false;
    if (activityMin != null && runner.activityCount < activityMin) return false;
    if (activityMax != null && runner.activityCount > activityMax) return false;
    if (dateFrom != null && runner.achievedAtEpoch < dateFrom) return false;
    if (dateTo != null && runner.achievedAtEpoch > dateTo) return false;
    return true;
  });
}

function sortValue(runner: NormalizedRunner, key: SortRule["key"]): string | number | null {
  switch (key) {
    case "achievementOrder": return runner.achievementOrder;
    case "nickname": return runner.nickname;
    case "age": return runner.age;
    case "gender": return runner.genderLabel;
    case "location": return runner.locationLabel;
    case "achievedAt": return runner.achievedAtEpoch;
    case "totalTime": return runner.totalTimeSeconds;
    case "activityCount": return runner.activityCount;
  }
}

export function sortRunners(runners: NormalizedRunner[], rules: SortRule[]) {
  return [...runners].sort((left, right) => {
    for (const rule of rules) {
      const a = sortValue(left, rule.key);
      const b = sortValue(right, rule.key);
      if (a == null && b == null) continue;
      if (a == null) return 1;
      if (b == null) return -1;
      let result = 0;
      if (typeof a === "string" && typeof b === "string") {
        result = a.localeCompare(b, "zh-Hant-TW", { numeric: true });
      } else {
        result = Number(a) - Number(b);
      }
      if (result !== 0) return rule.direction === "asc" ? result : -result;
    }
    return left.achievementOrder - right.achievementOrder;
  });
}

export function countBy<T extends string | number>(
  runners: NormalizedRunner[],
  getKey: (runner: NormalizedRunner) => T,
) {
  const counts = new Map<T, number>();
  for (const runner of runners) {
    const key = getKey(runner);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

export function ageGroupStats(runners: NormalizedRunner[]) {
  const byGroup = countBy(runners, (runner) => runner.ageGroup);
  return AGE_GROUPS.map((label) => ({ label, value: byGroup.get(label) ?? 0 }));
}

export function genderStats(runners: NormalizedRunner[]) {
  const labels = ["男性", "女性", "其他", "未知／未公開"];
  const byGender = countBy(runners, (runner) => runner.genderLabel);
  return labels.map((label) => ({ label, value: byGender.get(label) ?? 0 }));
}

export function prefectureStats(runners: NormalizedRunner[]) {
  const japan = runners.filter((runner) => runner.locationKind === "japan");
  const byCode = countBy(japan, (runner) => runner.prefectureCode ?? 0);
  return PREFECTURES.map((label, index) => ({
    code: index + 1,
    label,
    value: byCode.get(index + 1) ?? 0,
  }));
}

export function dailyAchievementStats(runners: NormalizedRunner[]) {
  const formatter = new Intl.DateTimeFormat("sv-SE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const byDate = countBy(runners, (runner) => formatter.format(new Date(runner.achievedAtEpoch)));
  const dates = [...byDate.keys()].sort();
  let cumulative = 0;
  return dates.map((date) => {
    const count = byDate.get(date) ?? 0;
    cumulative += count;
    return { date, count, cumulative };
  });
}

export function histogram(
  values: number[],
  requestedBins = 12,
): Array<{ start: number; end: number; count: number }> {
  if (values.length === 0) return [];
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return [{ start: min, end: max, count: values.length }];
  const bins = Math.min(requestedBins, Math.max(1, Math.ceil(Math.sqrt(values.length))));
  const width = (max - min) / bins;
  const result = Array.from({ length: bins }, (_, index) => ({
    start: min + index * width,
    end: min + (index + 1) * width,
    count: 0,
  }));
  for (const value of values) {
    const index = Math.min(Math.floor((value - min) / width), bins - 1);
    result[index].count += 1;
  }
  return result;
}

export function genderMedians(runners: NormalizedRunner[]) {
  return genderStats(runners).map(({ label }) => {
    const group = runners.filter((runner) => runner.genderLabel === label);
    return {
      label,
      time: median(group.map((runner) => runner.totalTimeSeconds)),
      activities: median(group.map((runner) => runner.activityCount)),
    };
  });
}

export function quartiles(values: number[]) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const q = (percentile: number) => {
    const index = (sorted.length - 1) * percentile;
    const lower = Math.floor(index);
    const fraction = index - lower;
    return sorted[lower + 1] == null
      ? sorted[lower]
      : sorted[lower] + fraction * (sorted[lower + 1] - sorted[lower]);
  };
  return [sorted[0], q(0.25), q(0.5), q(0.75), sorted[sorted.length - 1]];
}

export function activeFilterCount(filters: FilterState) {
  return (
    (filters.query ? 1 : 0) +
    filters.genders.length +
    filters.ageGroups.length +
    filters.locations.length +
    filters.prefectures.length +
    [
      filters.ageMin,
      filters.ageMax,
      filters.timeMinHours,
      filters.timeMaxHours,
      filters.activityMin,
      filters.activityMax,
      filters.dateFrom,
      filters.dateTo,
    ].filter(Boolean).length
  );
}
